
import { Link } from 'react-router-dom';
import { Filter, Calendar } from 'lucide-react';

interface FilterRecommendation {
  type: string;
  modelNumber: string;
  price: string;
  description: string;
  benefits: string[];
  image: string;
}

interface FilterRecommendationsSectionProps {
  recommendations: FilterRecommendation[];
  vehicleData: any;
  formSubmitted: boolean;
}

const FilterRecommendationsSection = ({ recommendations, vehicleData, formSubmitted }: FilterRecommendationsSectionProps) => {
  if (!formSubmitted) return null;
  
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    target.src = '/lovable-uploads/90959052-01e0-4de5-a75c-812d72605f31.png';
    console.log('Filter image failed to load, using fallback');
  };

  return (
    <section className="section-padding bg-white animate-fade-in">
      <div className="page-container">
        <div className="mb-10">
          <span className="inline-block px-3 py-1 text-xs font-semibold text-autocare-blue bg-autocare-blue/10 rounded-full mb-4">
            Vitano Filter Recommendations
          </span>
          <h2 className="text-3xl font-bold text-autocare-blue mb-4">Recommended Vitano Filters</h2>
          <p className="text-gray-600 max-w-2xl">
            Based on your {vehicleData?.year} {vehicleData?.make} {vehicleData?.model} {vehicleData?.engineType}, 
            here are our recommended Vitano {vehicleData?.filterType}s to maintain optimal performance.
          </p>
        </div>
        
        {/* Vitano Featured Product */}
        <div className="mb-12 bg-gradient-to-r from-autocare-gray-light to-white p-6 rounded-xl border border-autocare-gray-medium/20">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-full md:w-1/3">
              <img 
                src="/lovable-uploads/90959052-01e0-4de5-a75c-812d72605f31.png" 
                alt="Vitano Premium Filter"
                className="w-full h-auto max-w-[250px] mx-auto"
                onError={handleImageError}
              />
            </div>
            <div className="w-full md:w-2/3">
              <div className="flex justify-between items-center mb-2">
                <div>
                  <span className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">Recommended by professionals</span>
                  <h3 className="text-2xl font-bold text-autocare-blue">Vitano Professional Filter Technology</h3>
                </div>
                <img 
                  src="/lovable-uploads/90959052-01e0-4de5-a75c-812d72605f31.png" 
                  alt="Vitano Logo"
                  className="h-10 w-auto"
                  onError={handleImageError}
                />
              </div>
              <p className="text-gray-600 mb-4">
                Vitano Industry premium filters are engineered in Germany to provide exceptional protection and performance 
                for modern engines. With advanced filtration technology and superior materials, Vitano products ensure optimal 
                engine cleanliness, extended service intervals, and improved vehicle performance.
              </p>
              <div className="flex flex-wrap gap-3 mb-4">
                <span className="px-3 py-1 bg-autocare-blue/10 text-autocare-blue text-xs font-medium rounded-full">
                  German Engineered
                </span>
                <span className="px-3 py-1 bg-autocare-blue/10 text-autocare-blue text-xs font-medium rounded-full">
                  Precision Manufacturing
                </span>
                <span className="px-3 py-1 bg-autocare-blue/10 text-autocare-blue text-xs font-medium rounded-full">
                  Extended Service Life
                </span>
                <span className="px-3 py-1 bg-autocare-blue/10 text-autocare-blue text-xs font-medium rounded-full">
                  Premium Materials
                </span>
              </div>
              <a 
                href="https://vitano-industry.com/" 
                target="_blank"
                rel="noopener noreferrer" 
                className="text-autocare-blue hover:text-autocare-blue-light underline text-sm font-medium"
              >
                Learn more about Vitano Industry products
              </a>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {recommendations.map((filter, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all border border-autocare-gray-medium/30"
            >
              <div className="h-48 relative">
                <img 
                  src={filter.image}
                  alt={filter.type}
                  className="w-full h-full object-contain p-4"
                  onError={handleImageError}
                />
                {filter.modelNumber.includes('/PRO') && (
                  <div className="absolute top-2 right-2 bg-autocare-blue text-white text-xs font-bold px-2 py-1 rounded">
                    PREMIUM
                  </div>
                )}
              </div>
              
              <div className="p-5">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-lg font-semibold text-autocare-blue">{filter.type}</h3>
                    <p className="text-sm text-gray-500">Model: {filter.modelNumber}</p>
                  </div>
                  <span className="font-semibold text-autocare-blue">{filter.price}</span>
                </div>
                
                <p className="text-gray-600 mb-4 text-sm">{filter.description}</p>
                
                <div className="mb-4">
                  <h4 className="font-medium mb-2 text-sm">Benefits:</h4>
                  <ul className="space-y-1">
                    {filter.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start text-sm">
                        <span className="text-autocare-blue mt-1 mr-2 flex-shrink-0">•</span>
                        <span className="text-gray-600">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="flex flex-col space-y-2">
                  <Link 
                    to="/products" 
                    className="w-full py-2 px-4 rounded-lg bg-autocare-blue/20 text-autocare-blue font-medium hover:bg-autocare-blue/30 transition-colors flex justify-center items-center"
                  >
                    View Compatible Oils
                  </Link>
                  <Link 
                    to="/shops" 
                    className="w-full py-2 px-4 rounded-lg bg-autocare-blue text-white font-medium hover:bg-autocare-blue-light transition-colors flex justify-center items-center"
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Schedule Filter Change
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FilterRecommendationsSection;
